import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  DollarSign, FileText, CreditCard, TrendingUp, TrendingDown, 
  Search, User, RefreshCw, Plus, Trash2, AlertCircle, CheckCircle 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountStatement = ({ token, clients = [] }) => {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState(null);
  const [error, setError] = useState(null);
  
  // Balance Adjustment Modal
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    type: 'credit',
    amount: '',
    reason: ''
  });
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  // Filter clients based on search
  useEffect(() => {
    if (clientSearch.length > 0) {
      const search = clientSearch.toLowerCase();
      const filtered = clients.filter(c => 
        c.full_name?.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search) ||
        c.locker_number?.toLowerCase().includes(search)
      );
      setFilteredClients(filtered.slice(0, 10));
      setShowDropdown(true);
    } else {
      setFilteredClients([]);
      setShowDropdown(false);
    }
  }, [clientSearch, clients]);

  const selectClient = (client) => {
    setSelectedClientId(client.id);
    setClientSearch(client.full_name + ' - ' + (client.locker_number || client.email));
    setShowDropdown(false);
    fetchAccountStatement(client.id);
  };

  const fetchAccountStatement = async (clientId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin/clients/${clientId}/account-statement`, axiosConfig);
      setStatement(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar estado de cuenta');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdjustment = async () => {
    if (!selectedClientId || !adjustmentForm.amount) {
      setError('Por favor complete todos los campos requeridos');
      return;
    }

    setAdjustmentLoading(true);
    try {
      await axios.post(
        `${API}/admin/clients/${selectedClientId}/balance-adjustment`,
        {
          type: adjustmentForm.type,
          amount: parseFloat(adjustmentForm.amount),
          reason: adjustmentForm.reason
        },
        axiosConfig
      );
      
      // Reset form and close modal
      setAdjustmentForm({ type: 'credit', amount: '', reason: '' });
      setShowAdjustmentModal(false);
      
      // Refresh statement
      fetchAccountStatement(selectedClientId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear ajuste');
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const handleDeleteAdjustment = async (adjustmentId) => {
    if (!confirm('¿Está seguro de eliminar este ajuste?')) return;
    
    try {
      await axios.delete(`${API}/admin/balance-adjustments/${adjustmentId}`, axiosConfig);
      fetchAccountStatement(selectedClientId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar ajuste');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-VE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <FileText className="text-blue-600" />
            Estado de Cuenta
          </CardTitle>
          <CardDescription>
            Consulta el estado de cuenta y realiza ajustes de balance para los clientes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Client Search */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <Label className="text-blue-800 font-semibold mb-2 block">
              Seleccionar Cliente
            </Label>
            <div className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Buscar por nombre, email o casillero..."
                    className="pl-10"
                    data-testid="client-search-input"
                  />
                  <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                </div>
                {selectedClientId && (
                  <Button 
                    variant="outline" 
                    onClick={() => fetchAccountStatement(selectedClientId)}
                    className="gap-1"
                  >
                    <RefreshCw size={16} />
                    Actualizar
                  </Button>
                )}
              </div>
              
              {/* Dropdown */}
              {showDropdown && filteredClients.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredClients.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => selectClient(client)}
                      className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                    >
                      <User className="text-gray-400" size={20} />
                      <div className="flex-1">
                        <p className="font-medium">{client.full_name}</p>
                        <p className="text-sm text-gray-500">{client.email}</p>
                      </div>
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-mono">
                        {client.locker_number || 'Sin casillero'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-2">
              <AlertCircle className="text-red-600" size={20} />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="text-center py-8">
              <RefreshCw className="animate-spin mx-auto mb-2 text-blue-600" size={32} />
              <p className="text-gray-500">Cargando estado de cuenta...</p>
            </div>
          )}

          {/* Account Statement Display */}
          {statement && !loading && (
            <div className="space-y-6">
              {/* Client Info Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold">{statement.client.full_name}</h3>
                    <p className="text-blue-100">{statement.client.email}</p>
                    <p className="text-sm text-blue-200 mt-1">
                      Casillero: <span className="font-mono font-bold">{statement.client.locker_code || 'N/A'}</span>
                    </p>
                  </div>
                  <Button 
                    onClick={() => setShowAdjustmentModal(true)}
                    className="bg-white text-blue-600 hover:bg-blue-50"
                    data-testid="create-adjustment-btn"
                  >
                    <Plus size={16} className="mr-1" />
                    Ajuste de Balance
                  </Button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium">Total Facturado</p>
                  <p className="text-2xl font-bold text-blue-800">
                    {formatCurrency(statement.summary.total_invoiced)}
                  </p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-medium">Total Pagado</p>
                  <p className="text-2xl font-bold text-green-800">
                    {formatCurrency(statement.summary.total_paid)}
                  </p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-600 font-medium">Ajustes</p>
                  <p className="text-2xl font-bold text-purple-800">
                    {formatCurrency(statement.summary.total_adjustments)}
                  </p>
                </div>
                <div className={`rounded-lg p-4 ${
                  statement.summary.current_balance > 0 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-green-50 border border-green-200'
                }`}>
                  <p className={`text-sm font-medium ${
                    statement.summary.current_balance > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    Saldo Actual
                  </p>
                  <p className={`text-2xl font-bold ${
                    statement.summary.current_balance > 0 ? 'text-red-800' : 'text-green-800'
                  }`}>
                    {formatCurrency(statement.summary.current_balance)}
                  </p>
                </div>
              </div>

              {/* Invoices Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-3 font-semibold flex items-center gap-2">
                  <FileText size={18} />
                  Facturas ({statement.invoices?.length || 0})
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Nro. Factura</th>
                        <th className="p-2 text-left">Fecha</th>
                        <th className="p-2 text-right">Total</th>
                        <th className="p-2 text-right">Pagado</th>
                        <th className="p-2 text-right">Pendiente</th>
                        <th className="p-2 text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {statement.invoices?.map((inv) => (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="p-2 font-mono">{inv.invoice_number}</td>
                          <td className="p-2">{formatDate(inv.created_at)}</td>
                          <td className="p-2 text-right">{formatCurrency(inv.total)}</td>
                          <td className="p-2 text-right text-green-600">{formatCurrency(inv.paid_amount)}</td>
                          <td className="p-2 text-right text-red-600">{formatCurrency(inv.balance_due)}</td>
                          <td className="p-2 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              inv.status === 'paid' ? 'bg-green-100 text-green-800' :
                              inv.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {inv.status === 'paid' ? 'Pagada' : inv.status === 'pending' ? 'Pendiente' : 'Vencida'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {(!statement.invoices || statement.invoices.length === 0) && (
                        <tr>
                          <td colSpan="6" className="p-4 text-center text-gray-500">
                            No hay facturas registradas
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payments Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-3 font-semibold flex items-center gap-2">
                  <CreditCard size={18} />
                  Pagos Verificados ({statement.payments?.filter(p => p.status === 'verified').length || 0})
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Fecha</th>
                        <th className="p-2 text-left">Referencia</th>
                        <th className="p-2 text-left">Método</th>
                        <th className="p-2 text-right">Monto</th>
                        <th className="p-2 text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {statement.payments?.map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="p-2">{formatDate(payment.payment_date)}</td>
                          <td className="p-2 font-mono">{payment.reference_number}</td>
                          <td className="p-2 capitalize">{payment.payment_method}</td>
                          <td className="p-2 text-right font-semibold text-green-600">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="p-2 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              payment.status === 'verified' ? 'bg-green-100 text-green-800' :
                              payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {payment.status === 'verified' ? 'Verificado' : payment.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {(!statement.payments || statement.payments.length === 0) && (
                        <tr>
                          <td colSpan="5" className="p-4 text-center text-gray-500">
                            No hay pagos registrados
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Balance Adjustments Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 p-3 font-semibold flex items-center gap-2">
                  <DollarSign size={18} />
                  Ajustes de Balance ({statement.adjustments?.length || 0})
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Fecha</th>
                        <th className="p-2 text-left">Tipo</th>
                        <th className="p-2 text-right">Monto</th>
                        <th className="p-2 text-left">Razón</th>
                        <th className="p-2 text-left">Creado por</th>
                        <th className="p-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {statement.adjustments?.map((adj) => (
                        <tr key={adj.id} className="hover:bg-gray-50">
                          <td className="p-2">{formatDate(adj.created_at)}</td>
                          <td className="p-2">
                            <span className={`flex items-center gap-1 ${
                              adj.type === 'credit' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {adj.type === 'credit' ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                              {adj.type === 'credit' ? 'Crédito' : 'Débito'}
                            </span>
                          </td>
                          <td className={`p-2 text-right font-semibold ${
                            adj.amount > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {adj.amount > 0 ? '-' : '+'}{formatCurrency(Math.abs(adj.amount))}
                          </td>
                          <td className="p-2 text-gray-600">{adj.reason || '-'}</td>
                          <td className="p-2 text-gray-500 text-xs">{adj.created_by_name || 'Admin'}</td>
                          <td className="p-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAdjustment(adj.id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {(!statement.adjustments || statement.adjustments.length === 0) && (
                        <tr>
                          <td colSpan="6" className="p-4 text-center text-gray-500">
                            No hay ajustes de balance
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!statement && !loading && !error && (
            <div className="text-center py-12 text-gray-500">
              <User size={48} className="mx-auto mb-4 text-gray-300" />
              <p>Selecciona un cliente para ver su estado de cuenta</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Balance Adjustment Modal */}
      {showAdjustmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="text-blue-600" />
                Crear Ajuste de Balance
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label>Cliente</Label>
                <p className="font-semibold">{statement?.client.full_name}</p>
                <p className="text-sm text-gray-500">{statement?.client.email}</p>
              </div>
              
              <div>
                <Label>Tipo de Ajuste</Label>
                <Select 
                  value={adjustmentForm.type}
                  onValueChange={(v) => setAdjustmentForm({...adjustmentForm, type: v})}
                >
                  <SelectTrigger data-testid="adjustment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">
                      <span className="flex items-center gap-2 text-green-600">
                        <TrendingDown size={14} />
                        Crédito (reduce saldo pendiente)
                      </span>
                    </SelectItem>
                    <SelectItem value="debit">
                      <span className="flex items-center gap-2 text-red-600">
                        <TrendingUp size={14} />
                        Débito (aumenta saldo pendiente)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Monto ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={adjustmentForm.amount}
                  onChange={(e) => setAdjustmentForm({...adjustmentForm, amount: e.target.value})}
                  placeholder="0.00"
                  data-testid="adjustment-amount-input"
                />
              </div>
              
              <div>
                <Label>Razón / Descripción</Label>
                <Textarea
                  value={adjustmentForm.reason}
                  onChange={(e) => setAdjustmentForm({...adjustmentForm, reason: e.target.value})}
                  placeholder="Ej: Descuento por fidelidad, corrección de factura, etc."
                  rows={3}
                  data-testid="adjustment-reason-input"
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAdjustmentModal(false);
                  setAdjustmentForm({ type: 'credit', amount: '', reason: '' });
                }}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateAdjustment}
                disabled={adjustmentLoading || !adjustmentForm.amount}
                data-testid="save-adjustment-btn"
              >
                {adjustmentLoading ? 'Guardando...' : 'Guardar Ajuste'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountStatement;
